import Vue from "vue";
import { http } from "@/communication";
import { unionBy } from "lodash";

/**
 * create a CRUD based resource module in vuex
 *
 * @param {String} name
 */
export function moduleFactory(name = "resource") {
  return {
    namespaced: true,
    state: { [`${name}s`]: [], meta: {}, count: 0 },
    actions: {
      read({ commit, getters }, data) {
        let filter = [];
        let appends = [];
        if (data && typeof data === "object") {
          if (Array.isArray(data)) {
            filter = data;
          } else {
            appends = data.appends ? data.appends : [];
            filter = data.filter ? data.filter : [];
          }
        }
        return http
          .get(getters.resourceUrl(), {
            params: {
              filter,
              appends
            }
          })
          .then(({ data }) => {
            commit("update", data);
          })
          .catch(error => {
            console.log(`${name}s error`, error);
          });
      },
      count({ commit, getters }, filter = []) {
        return http
          .get(getters.resourceUrl(), {
            params: {
              filter: [...filter, { name: "count" }]
            }
          })
          .then(({ data }) => {
            commit("count", data);
          })
          .catch(error => {
            console.log(`${name}s error`, error);
          });
      },
      create(store, { url, data, options }) {
        return http.post(url, data, options).then(response => {
          if (response.data && response.data.id !== undefined) {
            store.commit("update", [response.data]);
          }
          return response;
        });
      },
      update(store, { url, data }) {
        let method = data instanceof FormData ? "post" : "patch";
        return http[method](url, data).then(response => {
          store.commit("update", [response.data]);
          return response;
        });
      },
      delete(store, { url }) {
        return http.delete(url);
      },
      meta({ commit, getters }) {
        return http
          .get(getters.resourceUrl() + "meta")
          .then(({ data }) => {
            commit("meta", data);
          })
          .catch(() => {
            console.warn(`${name}s meta error`);
          });
      }
    },
    mutations: {
      create(state, newResource) {
        Vue.set(state, `${name}s`, [...state[`${name}s`], newResource]);
      },
      update(state, resources) {
        const updateResources = Array.isArray(resources)
          ? resources
          : [resources];
        const unionResources = unionBy(
          updateResources,
          state[`${name}s`],
          resource => resource.id
        );
        Vue.set(state, `${name}s`, unionResources);
      },
      meta(state, meta) {
        state.meta = meta;
      },
      delete(state, id) {
        state[`${name}s`].splice(
          state[`${name}s`].findIndex(resource => resource.id == id),
          1
        );
      },
      deleteAll(state) {
        state[`${name}s`] = [];
      },
      updateModel(state, { modelId, attributePath, data }) {
        let modelIndex = state[`${name}s`].findIndex(
          model => model.id === modelId
        );

        if (modelIndex >= 0) {
          let segments = attributePath.split(".");

          let attributeToChange = segments.pop();

          let value = state[`${name}s`][modelIndex];
          segments.forEach(segment => {
            value = value[segment];
          });

          Vue.set(value, attributeToChange, data);
        }
      },
      count(state, count) {
        state.count = count;
      }
    },
    getters: {
      [`${name}s`](state) {
        return state[`${name}s`];
      },
      find: state => (value, property = "id") => {
        return state[`${name}s`].filter(resource => {
          return resource[property] === value;
        })[0];
      },
      resourceUrl: () => (resource = null) => {
        return resource === null
          ? `/api/v1/${name}/`
          : `/api/v1/${name}/${resource.id}`;
      },
      options: state => attribute => {
        return state.meta["attributes-options"] !== undefined &&
          state.meta["attributes-options"][attribute] !== undefined
          ? state.meta["attributes-options"][attribute]
          : null;
      },
      createUrl({ meta }) {
        return meta["create-url"] !== undefined ? meta["create-url"] : null;
      },
      createRedirectUrl({ meta }) {
        return meta["create-redirect-url"] !== undefined
          ? meta["create-redirect-url"]
          : null;
      },
      updateRedirectUrl({ meta }) {
        return meta["update-redirect-url"] !== undefined
          ? meta["update-redirect-url"]
          : null;
      },
      count({ count }) {
        return count;
      }
    }
  };
}
